import React, { useCallback, useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AskFrankDesk, {
    DeskRibbon,
    DeskTitle,
    DeskNote,
    DeskPanel,
    DeskStat,
    DeskEmpty,
    Pill,
    formatNumber,
    formatDate,
    DESK_BUTTON_CLASS,
    DESK_PRIMARY_CLASS,
    DESK_PRIMARY_STYLE,
} from '../../../../components/staff/askfrank/AskFrankDesk';
import {
    getAskFrankInsights,
    getAskFrankSources,
    getAskFrankBrain,
    getAskFrankEntities,
    getAskFrankUnanswered,
    getAskFrankNewsStatus,
    syncAskFrankNews,
} from '../../../../services/api';

function AskFrankOverview({ userData }) {
    const [insights, setInsights] = useState(null);
    const [totals, setTotals] = useState({});
    const [brain, setBrain] = useState(null);
    const [entityTotals, setEntityTotals] = useState({});
    const [unanswered, setUnanswered] = useState([]);
    const [news, setNews] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        const [insightRes, sourceRes, brainRes, entityRes, unansweredRes, newsRes] = await Promise.all([
            getAskFrankInsights(),
            getAskFrankSources(),
            getAskFrankBrain(),
            getAskFrankEntities({}),
            getAskFrankUnanswered(),
            getAskFrankNewsStatus(),
        ]);

        if (insightRes.success) setInsights(insightRes);
        if (sourceRes.success) setTotals(sourceRes.totals || {});
        if (brainRes.success) setBrain(brainRes);
        if (entityRes.success) setEntityTotals(entityRes.totals || {});
        if (unansweredRes.success) setUnanswered(unansweredRes.queries || []);
        if (newsRes.success) setNews(newsRes);

        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    if (userData && !userData.hasAdminRole) return <Navigate to="/staff" replace />;

    const checkFeed = async () => {
        setBusy(true);
        const res = await syncAskFrankNews(2);
        setBusy(false);

        if (res.success) {
            toast.success(res.message || 'Feed checked');
            load();
        } else {
            toast.error(res.message || 'Could not reach the news feed');
        }
    };

    const performance = insights?.performance || {};
    const recent = insights?.recent || [];

    return (
        <AskFrankDesk userData={userData}>
            <DeskRibbon>
                <DeskTitle>AskFrank Overview</DeskTitle>
                <DeskNote>How Frank has been doing, and what he has been getting wrong.</DeskNote>

                <button type="button" onClick={load} className={`${DESK_BUTTON_CLASS} ml-auto`}>
                    Refresh
                </button>
            </DeskRibbon>

            {loading ? (
                <DeskPanel title="Health"><DeskEmpty>Reading the shelves...</DeskEmpty></DeskPanel>
            ) : (
                <>
                    <DeskPanel title="Health">
                        <div className="flex flex-wrap gap-2">
                            <DeskStat label="Searches (7d)" value={formatNumber(performance.total)} />
                            <DeskStat
                                label="Avg response"
                                value={`${formatNumber(performance.avgMs)}ms`}
                                tone={performance.avgMs > 800 ? 'warn' : 'good'}
                            />
                            <DeskStat
                                label="No results"
                                value={formatNumber(performance.misses)}
                                tone={performance.misses > 0 ? 'warn' : 'good'}
                            />
                            <DeskStat label="Pages indexed" value={formatNumber(totals.indexed)} />
                            <DeskStat label="Queued" value={formatNumber(totals.queued)} />
                            <DeskStat
                                label="Crawl errors"
                                value={formatNumber(totals.errored)}
                                tone={totals.errored > 0 ? 'bad' : 'good'}
                            />
                            <DeskStat label="Written entries" value={formatNumber(entityTotals.total)} />
                            <DeskStat label="Lexicon terms" value={formatNumber(brain?.lexicon?.total)} />
                        </div>

                        {(totals.pending > 0 || entityTotals.candidates > 0) && (
                            <div className="mt-3 flex flex-wrap items-center gap-3 border border-gray-200 bg-white px-3 py-2 text-[12px] text-gray-700">
                                <span className="font-['Jersey_10'] text-[15px] uppercase tracking-[1px] text-maroon">
                                    Waiting on you
                                </span>
                                {totals.pending > 0 && (
                                    <Link to="/staff/admin/askfrank/sources" className="underline">
                                        {totals.pending} source{totals.pending === 1 ? '' : 's'} pending review
                                    </Link>
                                )}
                                {entityTotals.candidates > 0 && (
                                    <Link to="/staff/admin/askfrank/knowledge" className="underline">
                                        {entityTotals.candidates} name{entityTotals.candidates === 1 ? '' : 's'} waiting
                                    </Link>
                                )}
                            </div>
                        )}
                    </DeskPanel>

                    <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                        <DeskPanel title="Recent questions" padding="p-0">
                            {recent.length === 0 ? (
                                <DeskEmpty>Nobody has asked anything yet.</DeskEmpty>
                            ) : (
                                <div className="max-h-[420px] overflow-y-auto">
                                    {recent.map((row, index) => (
                                        <div
                                            key={`${row.createdAt}-${index}`}
                                            className="flex flex-wrap items-center gap-2 border-b border-gray-200 px-3 py-[6px] text-[12px] last:border-b-0"
                                        >
                                            <span className="min-w-0 flex-1 truncate text-gray-800">{row.query}</span>
                                            {row.questionType && <Pill>{row.questionType}</Pill>}
                                            <Pill tone={row.resultCount === 0 ? 'red' : 'green'}>
                                                {row.resultCount === 0 ? 'nothing' : `${row.resultCount} hits`}
                                            </Pill>
                                            <span className="w-[52px] shrink-0 text-right text-[11px] text-gray-400">
                                                {row.responseMs}ms
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </DeskPanel>

                        <DeskPanel
                            title="Came up short"
                            padding="p-0"
                            right={<span className="text-[11px] uppercase text-white/70">last 60 days</span>}
                        >
                            <p className="border-b border-gray-200 px-3 py-2 text-[12px] text-gray-500">
                                Questions Frank answered thinly or not at all. Each one is a missing source, a missing
                                keyword, or a phrasing he does not understand yet.
                            </p>

                            {unanswered.length === 0 ? (
                                <DeskEmpty>Nothing on the pile.</DeskEmpty>
                            ) : (
                                <div className="max-h-[372px] overflow-y-auto">
                                    {unanswered.map(row => (
                                        <div
                                            key={row.normalised}
                                            className="flex flex-wrap items-center gap-2 border-b border-gray-200 px-3 py-[6px] text-[12px] last:border-b-0"
                                        >
                                            <span className="min-w-0 flex-1 truncate text-gray-800">{row.query}</span>
                                            {row.intent && <Pill>{row.intent}</Pill>}
                                            {row.answerKind && <Pill tone="yellow">{row.answerKind}</Pill>}
                                            <span className="shrink-0 text-[11px] text-gray-400">asked {row.hits}x</span>
                                            <Link
                                                to={`/staff/admin/askfrank/lab?q=${encodeURIComponent(row.query)}`}
                                                className="shrink-0 border border-gray-300 bg-white px-2 py-[2px] text-[10px] uppercase text-gray-700 hover:bg-gray-100"
                                            >
                                                Explain
                                            </Link>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </DeskPanel>
                    </div>

                    <DeskPanel title="Habbo Origins news">
                        <p className="text-[12px] text-gray-500">
                            Official articles are imported, not crawled, so the generic crawler never touches habbo.com.
                            The archive ships with the schema and new articles arrive on their own every 6 hours.
                        </p>

                        <div className="mt-3 flex flex-wrap items-center gap-3">
                            <span className="text-[13px] text-gray-800">
                                {formatNumber(news?.stats?.total)} articles
                                {news?.stats?.oldest && news?.stats?.newest
                                    ? `, covering ${formatDate(news.stats.oldest)} to ${formatDate(news.stats.newest)}`
                                    : ''}
                                {news?.stats?.missingIds > 0 ? ` (${news.stats.missingIds} missing a Habbo ID)` : ''}
                            </span>

                            <button
                                type="button"
                                disabled={busy}
                                onClick={checkFeed}
                                className={`${DESK_PRIMARY_CLASS} ml-auto`}
                                style={DESK_PRIMARY_STYLE}
                            >
                                {busy ? 'Checking...' : 'Check feed now'}
                            </button>
                        </div>

                        {news?.recent?.length > 0 && (
                            <div className="mt-3 max-h-[180px] overflow-y-auto border border-gray-200 bg-white">
                                {news.recent.map(item => (
                                    <div key={item.slug} className="flex items-center gap-2 border-b border-gray-200 px-2 py-1 last:border-b-0">
                                        <span className="w-[84px] shrink-0 text-[10px] uppercase text-gray-400">
                                            {formatDate(item.publishedAt)}
                                        </span>
                                        <span className="min-w-0 flex-1 truncate text-[12px] text-gray-700">{item.title}</span>
                                        <span className="shrink-0 text-[10px] uppercase text-gray-400">{item.importedFrom}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </DeskPanel>
                </>
            )}
        </AskFrankDesk>
    );
}

export default AskFrankOverview;
